"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import CartDrawer from "@/components/cart/CartDrawer";
import { Toaster } from "@/components/shared/Toaster";
import { useCartStore } from "@/lib/store/cartStore";
import { OfflineBanner } from "@/components/ui/OfflineBanner";
import { NewVersionBanner } from "@/components/ui/NewVersionBanner";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { StorefrontUnavailable } from "@/components/shop/StorefrontUnavailable";
import GoogleTag from "@/components/analytics/GoogleTag";
import { MaintenanceGate } from "@/components/maintenance/MaintenanceGate";

interface BrandLayoutClientProps {
  children: React.ReactNode;
  domain?: string;
}

export default function BrandLayoutClient({ children, domain }: BrandLayoutClientProps) {
  const { initializeSession, sessionId } = useCartStore();
  const pathname = usePathname();

  // POS pages have the update badge built into the header — don't double-render
  const isPOS = pathname?.includes("/pos");

  // Auth / owner routes must NEVER be covered by the storefront-unavailable
  // overlay — otherwise a brand owner whose subscription lapsed can't even reach
  // the sign-in page to log in and pay (the exact lockout brands reported). The
  // sign-in page is under this same [domain] layout, so it must be excluded here.
  const isAuthRoute = !!pathname && (
    pathname.includes("/signin") ||
    pathname.includes("/sign-in") ||
    pathname.includes("/signup") ||
    pathname.includes("/sign-up") ||
    pathname.includes("/login") ||
    pathname.includes("/register") ||
    pathname.includes("/forgot-password") ||
    pathname.includes("/reset-password") ||
    pathname.includes("/verify")
  );

  // Customer storefront lock: if this brand's subscription has lapsed (or its
  // trial is over), show a friendly "store unavailable" overlay so shoppers
  // can't browse or order. POS + auth routes are excluded. Fails open — an
  // unknown subdomain returns { blocked: false }, never hiding a real shop.
  const access = useQuery(
    api.subscriptions.subscriptions.getBrandAccessStatusBySubdomain,
    !isPOS && !isAuthRoute && domain ? { subdomain: domain } : "skip",
  );
  const storeBlocked = !isPOS && !isAuthRoute && access?.blocked === true;

  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  useEffect(() => {
    if (sessionId) {
      console.log("[Cart] Session initialized:", sessionId);
    }
  }, [sessionId]);

  return (
    // The maintenance screen a Kuttos admin switches on covers the shop AND the
    // till here; sign-in stays open (components/maintenance/MaintenanceGate).
    <MaintenanceGate subdomain={domain}>
      <div
        data-theme="marketplace"
        className="marketplace-scope min-h-screen"
      >
        {children}
        <CartDrawer />
        <Toaster />
        <OfflineBanner />
        {!isPOS && <NewVersionBanner />}
        {storeBlocked && <StorefrontUnavailable />}
        {/* Brand's own Google tag (GA4 / Ads). Rides the access query above, so
            no extra Convex read; renders nothing until the brand configures IDs.
            Excluded from POS + auth routes — staff screens aren't shopper traffic. */}
        {!isPOS && !isAuthRoute && <GoogleTag tracking={(access as any)?.tracking} />}
      </div>
    </MaintenanceGate>
  );
}
